import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
import { BridgeMessages } from '../../nextray/Core/Bridge';
import { APP_CONFIG, LoaderService, ToastService } from '../core';
import { CaseService, MainCanvasLoaderService, ProductService, UserService } from '../services';
import { BaseComponent, ConfirmationComponent } from '../shared';
import { CanvasTabsetComponent } from './canvas-tabset';
import { LayersComponent } from './layers';
import { MagnifierComponent } from './magnifier';


/**
* Canvas view component.
*/
@Component({
	selector: 'canvas3d',
	templateUrl: './canvas3d.component.html',
	imports: [
		DecimalPipe,
		LayersComponent,
		MagnifierComponent,
		CanvasTabsetComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class Canvas3DComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly caseSrv = inject(CaseService);
	private readonly userSrv = inject(UserService);
	private readonly toastSrv = inject(ToastService);
	private readonly prodSrv = inject(ProductService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly mainCanvLoadSrv = inject(MainCanvasLoaderService);

	isJPSExtension = false;


	ngOnInit(): void {
		this.userSrv.isJPSExtension().subscribe(res => this.isJPSExtension = res);
		this.initCanvas();
	}

	ngAfterViewInit(): void {
		this.canvasSrv.addEventListener('onResult', this.osteotomyLengthError);
		this.canvasSrv.addEventListener('onResult', this.jpsUpgrading);
	}

	private initCanvas(): void {
		this.loaderSrv.show();
		this.mainCanvLoadSrv.init().pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({ error: (err: Error) => this.handlePlanError(err) });
	}

	/**
	* Event listener for osteotomy length error.
	*/
	osteotomyLengthError = (event: { args: keyof BridgeMessages }) => {
		if (event.args === 'osteotomyLengthError') {
			this.toastSrv.showWarning(this.labels().CANVAS3D_COMPONENT_OSTEOTOMY_LENGTH_ERROR);
		}
	}

	/**
	* Event listener for JPS upgrading.
	*/
	jpsUpgrading = (event: { args: keyof BridgeMessages }) => {
		if (event.args === 'restore') {
			if (this.isJPSExtension && this.isPlateInserted && !this.hasJPSExtended) {
				this.openUpgradeJPSModal();
			}
			else {
				this.prodSrv.initJPSExtended(this.isJPSExtension);
			}
		}
	}

	/**
	* Check if magnifier is visible.
	*/
	get isMagnifierVisible(): boolean {
		return (this.stateDescription?.isWorkflowRunning == this.workflowEnum.insertMechanicalAxis || this.stateDescription?.isWorkflowRunning == this.workflowEnum.insertContralateral) && (this.activeViewState != this.activeViewStateEnum.multiple) && this.stateDescription.isPointsPositioningRunning;
	}

	/**
	* Check if is Ap view active in multiple state.
	*/
	get isMultipleAP(): boolean {
		return this.activeViewState === this.activeViewStateEnum.multiple && this.activeView === this.activeViewEnum.AP;
	}

	/**
	* Fit to view the current active view.
	*/
	fitToView(): void {
		this.canvasSrv.dispatch('fitToView');
	}

	/**
	* Zoom in the current active view.
	*/
	zoomIn(): void {
		this.canvasSrv.dispatch('zoomIn');
	}

	/**
	* Zoom out the current active view.
	*/
	zoomOut(): void {
		this.canvasSrv.dispatch('zoomOut');
	}

	private handlePlanError(err: Error): void {
		switch (err.message) {
			case 'PlanNotFound': {
				this.openInvalidPlanModal(this.labels().PLAN_DELETED_WARNING_MESSAGE);
				break;
			}
			case 'PlanNotValid': {
				this.openInvalidPlanModal(this.labels().INVALID_PLAN_MODAL_MESSAGE);
				break;
			}
			case 'WebglDisabled': {
				this.openInvalidPlanModal(this.labels().WEBGL_INVALID_WARNING_MESSAGE);
				break;
			}
			case 'ScreenResolutionInvalid': {
				this.openInvalidPlanModal(this.labels().RESOLUTION_INVALID_WARNING_MESSAGE);
				break;
			}
			default:
				throw err;
		}
	}

	private openInvalidPlanModal(message: string): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().INVALID_PLAN_MODAL_TITLE,
			message,
			confirmBtn: this.labels().INVALID_PLAN_MODAL_CONFIRM_BTN,
			isCancelBtn: false
		};
		modalRef.result.then(() => globalThis.location.href = this.config.patplanSite);
	}

	private openUpgradeJPSModal(): void {
		const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, {
			centered: true, backdrop: 'static'
		});
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().UPGRADE_JPS_MODAL_TITLE,
			message: this.labels().UPGRADE_JPS_MODAL_MESSAGE,
			confirmBtn: this.labels().UPGRADE_JPS_MODAL_CONFIRM_BTN,
			cancelBtn: this.labels().UPGRADE_JPS_MODAL_CANCEL_BTN
		};
		modalRef.result.then(() => this.upgradeJPS(), () => this.rejectUpgradeJPS(),
		);
	}
	private upgradeJPS(): void {
		this.prodSrv.initJPSExtended(true);
		const plateId = this.plate!.id;
		this.prodSrv.getScrewList(plateId, true).subscribe(list => this.canvasSrv.dispatch('updateScrews', list));
	}
	private rejectUpgradeJPS(): void {
		this.caseSrv.setReadOnly();
		this.prodSrv.initJPSExtended(false);
	}


	ngOnDestroy(): void {
		this.canvasSrv.removeEventListener('onResult', this.osteotomyLengthError);
	}

}
